
import React, { Component, ErrorInfo, ReactNode } from "react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { reportError } from "@/utils/monitoring";
import { supabase } from "@/integrations/supabase/client";

/**
 * A global error boundary that reports errors to Sentry and system_alerts.
 */
interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}
export class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): Pick<State, 'hasError' | 'error'> {
    return { hasError: true, error };
  }

  public async componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Report error to Sentry with enhanced context
    reportError(error, {
      componentStack: errorInfo?.componentStack,
      location: window.location.href,
      timestamp: new Date().toISOString()
    });
    
    if (import.meta.env.MODE === "development") {
      console.error("Uncaught error (GlobalErrorBoundary):", error, errorInfo);
    }

    // Log to system_alerts table if connected to Supabase
    let userId = null;
    try {
      const session = await supabase.auth.getSession();
      userId = session?.data?.session?.user?.id || null;
    } catch (e) {
      console.warn("Could not get user for GlobalErrorBoundary alert logging");
    }

    try {
      await supabase.functions.invoke('log-system-alert', {
        body: {
          source: 'frontend_global_error_boundary',
          type: 'UNCAUGHT_EXCEPTION',
          severity: 'CRITICAL',
          message: error.message,
          details: {
            error: { name: error.name, message: error.message, stack: error.stack },
            componentStack: errorInfo?.componentStack,
          },
          user_id: userId,
        }
      });
    } catch (alertError) {
      console.error("Failed to log system alert for global error boundary:", alertError);
    }
  }

  public render() {
    if (this.state.hasError) {
      return (
        <Alert variant="destructive" className="m-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription className="mt-2">
            <p className="mb-2">
              An unexpected error occurred. Our team has been notified.
            </p>
            {import.meta.env.MODE === "development" && this.state.error && (
              <details className="mb-4 text-sm bg-red-50 p-2 rounded border border-red-200">
                <summary className="cursor-pointer font-medium">Error Details (Dev Mode)</summary>
                <pre className="mt-1 whitespace-pre-wrap break-all">
                  Error: {this.state.error.name}: {this.state.error.message}
                  {this.state.error.stack && `\nStack:\n${this.state.error.stack}`}
                  {this.state.errorInfo?.componentStack && `\nComponent Stack:\n${this.state.errorInfo.componentStack}`}
                </pre>
              </details>
            )}
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              className="flex items-center gap-2"
            >
              <RefreshCcw className="h-4 w-4" />
              Reload page
            </Button>
          </AlertDescription>
        </Alert>
      );
    }
    return this.props.children;
  }
}
